
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { extractSerialNumberFromImage } from './services/geminiService';
import { type Step, type FormData } from './types';
import Spinner from './components/Spinner';
import { Icon } from './components/Icon';

interface Base64Image {
  filename: string;
  content: string; // base64 encoded string
}

const initialFormData: FormData = {
  serialNumber: '',
  problemDescription: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
};

const compressImage = (file: File): Promise<Base64Image> => {
  return new Promise((resolve, reject) => {
    const MAX_WIDTH = 1280;
    const MAX_HEIGHT = 1280;
    const QUALITY = 0.7; // 70% quality

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      if (!event.target?.result) {
        return reject(new Error('Failed to read file.'));
      }
      const img = new Image();
      img.src = event.target.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return reject(new Error('Could not get canvas context for image compression.'));
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error('Canvas to Blob conversion failed.'));
            }
            const blobReader = new FileReader();
            blobReader.readAsDataURL(blob);
            blobReader.onloadend = () => {
              const base64data = blobReader.result as string;
              resolve({
                filename: file.name,
                content: base64data.split(',')[1],
              });
            };
            blobReader.onerror = (error) => reject(error);
          },
          'image/jpeg',
          QUALITY
        );
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};


const App: React.FC = () => {
  const [step, setStep] = useState<Step>('CAPTURE_SN');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [snImageFile, setSnImageFile] = useState<File | null>(null);
  const [snImageUrl, setSnImageUrl] = useState<string>('');
  const [errorImages, setErrorImages] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showManualSnInput, setShowManualSnInput] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);

  const errorFileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    try {
      const savedData = localStorage.getItem('ricohContactDetails');
      if (savedData) {
        const { contactName, contactPhone, contactEmail } = JSON.parse(savedData);
        if (contactName && contactPhone && contactEmail) {
          setFormData(prev => ({ ...prev, contactName, contactPhone, contactEmail }));
          setRememberMe(true);
        }
      }
    } catch (error) {
      console.error("Failed to load contact details from localStorage", error);
      // If parsing fails or data is invalid, remove the item.
      localStorage.removeItem('ricohContactDetails');
    }
  }, []);

  const processImageFile = async (file: File) => {
    if (file) {
      setSnImageFile(file);
      setSnImageUrl(URL.createObjectURL(file));
      setError('');
      setIsLoading(true);
      setStep('VERIFY_SN');
      try {
        const serialNumber = await extractSerialNumberFromImage(file);
        setFormData((prev) => ({ ...prev, serialNumber }));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'En ukendt fejl opstod');
        setStep('CAPTURE_SN');
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  const openCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Kamera-funktionen understøttes ikke af din browser.");
        return;
    }

    try {
        const constraints: MediaStreamConstraints = {
            video: { 
                facingMode: 'environment',
                width: { ideal: 1920 },
                height: { ideal: 1080 }
            }
        };
        let mediaStream: MediaStream;
        try {
            // First, try to get the back camera with ideal constraints
            mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (err) {
            console.warn("Could not get environment camera with ideal constraints, trying with simpler constraints.", err);
            // If that fails, try with simpler constraints
            mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        }
        setStream(mediaStream);
        setIsCameraOpen(true);
    } catch (err) {
        // If environment camera fails entirely, try any camera
        console.warn("Could not get environment camera, trying any available camera.", err);
        try {
             const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
             setStream(mediaStream);
             setIsCameraOpen(true);
        } catch (finalErr) {
             console.error("Error accessing any camera:", finalErr);
             setError("Kunne ikke få adgang til kameraet. Tjek venligst dine browser-tilladelser og sørg for at du bruger en sikker forbindelse (HTTPS).");
        }
    }
  };

  useEffect(() => {
    if (isCameraOpen && stream && videoRef.current) {
      const videoElement = videoRef.current;
      videoElement.srcObject = stream;
      
      videoElement.onloadedmetadata = () => {
        videoElement.play().catch(e => {
            console.error("Video play failed. User interaction might be required.", e);
        });
      };
    }
  }, [isCameraOpen, stream]);

  const closeCamera = useCallback(() => {
    if (stream) {
        stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
    setIsCameraOpen(false);
  }, [stream]);

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (context) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(async (blob) => {
                if (blob) {
                    const file = new File([blob], "sn_capture.jpg", { type: "image/jpeg" });
                    await processImageFile(file);
                }
            }, 'image/jpeg');
        }
        closeCamera();
    }
  };

  const handleErrorImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
        const newFiles = Array.from(files);
        if (errorImages.length + newFiles.length > 5) {
            setError('Du kan maksimalt vedhæfte 5 billeder.');
            return;
        }
        setError('');
        setErrorImages(prev => [...prev, ...newFiles]);
    }
  };
  
  const removeErrorImage = (indexToRemove: number) => {
    setErrorImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleOcrVerification = () => {
    if (formData.serialNumber.trim() === '') {
      setError('Serienummer må ikke være tomt.');
      return;
    }
    setError('');
    setStep('DETAILS');
  };

  const resetState = useCallback(() => {
    // Keep contact details if they were loaded from localStorage
    const savedContactDetails = rememberMe ? {
      contactName: formData.contactName,
      contactPhone: formData.contactPhone,
      contactEmail: formData.contactEmail,
    } : {};

    setStep('CAPTURE_SN');
    setFormData({ ...initialFormData, ...savedContactDetails });
    setSnImageFile(null);
    setSnImageUrl('');
    setErrorImages([]);
    setIsLoading(false);
    setError('');
    setShowManualSnInput(false);
    closeCamera();
    if (errorFileInputRef.current) errorFileInputRef.current.value = '';
  }, [closeCamera, formData.contactEmail, formData.contactName, formData.contactPhone, rememberMe]);

  const handleManualSnSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formData.serialNumber.trim() === '') {
      setError('Serienummer må ikke være tomt.');
      return;
    }
    setError('');
    setStep('DETAILS');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
    setStep('SUBMITTING');

    try {
      if (rememberMe) {
        try {
          const contactDetails = {
            contactName: formData.contactName,
            contactPhone: formData.contactPhone,
            contactEmail: formData.contactEmail,
          };
          localStorage.setItem('ricohContactDetails', JSON.stringify(contactDetails));
        } catch (error) {
          console.error("Failed to save contact details to localStorage", error);
        }
      } else {
        try {
          localStorage.removeItem('ricohContactDetails');
        } catch (error) {
          console.error("Failed to remove contact details from localStorage", error);
        }
      }
      
      const imagePromises = errorImages.map(file => compressImage(file));
      
      const attachments = await Promise.all(imagePromises);

      const payload = { ...formData, attachments };

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Fejl ${response.status}: Der opstod et problem ved afsendelse.`;
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            console.error("Could not parse server error as JSON.", e);
        }
        throw new Error(errorMessage);
      }

      setStep('SUBMITTED');

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'En ukendt serverfejl opstod.';
      setError(errorMessage);
      setStep('DETAILS'); // Go back to the form so the user can try again
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderError = () => {
      if (!error) return null;
      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
        </div>
      );
  }

  const renderStep = () => {
    switch (step) {
      case 'CAPTURE_SN':
        if (showManualSnInput) {
          return (
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2 text-gray-800">Trin 1: Indtast Serienummer</h2>
              <p className="text-gray-600 mb-6">Indtast maskinens serienummer manuelt.</p>
              {renderError()}
              <form onSubmit={handleManualSnSubmit} className="space-y-4">
                <div>
                  <label htmlFor="manualSerialNumber" className="sr-only">Serienummer</label>
                  <input
                    id="manualSerialNumber"
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg text-gray-900 placeholder:text-gray-500"
                    placeholder="Indtast serienummer"
                    required
                    autoFocus
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="button" onClick={() => { setShowManualSnInput(false); setError(''); }} className="w-full bg-gray-200 text-gray-800 font-bold py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300">
                    Tilbage
                  </button>
                  <button type="submit" className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300">
                    Fortsæt
                  </button>
                </div>
              </form>
            </div>
          );
        }
        return (
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-2 text-gray-800">Trin 1: Scan Serienummer</h2>
            <p className="text-gray-600 mb-6">Ret kameraet mod mærkatet med maskinens serienummer.</p>
            {renderError()}
            <button
              onClick={openCamera}
              className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-300 text-lg shadow-md"
            >
              <Icon type="camera" className="w-7 h-7" />
              Åbn Kamera
            </button>
             <div className="mt-4">
                <button
                    onClick={() => setShowManualSnInput(true)}
                    className="text-sm text-gray-600 hover:text-blue-600 hover:underline"
                >
                    Eller indtast serienummer manuelt
                </button>
            </div>
          </div>
        );
      
      case 'VERIFY_SN':
        return (
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-center text-gray-800">Trin 2: Bekræft Serienummer</h2>
            {isLoading ? (
              <div className="flex flex-col items-center gap-4">
                <Spinner />
                <p className="text-gray-600 animate-pulse">Analyserer billede...</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {renderError()}
                <p className="text-gray-600 text-center">Vi har fundet følgende serienummer. Er det korrekt?</p>
                {snImageUrl && <img src={snImageUrl} alt="Serienummer" className="rounded-lg max-h-60 w-auto mx-auto shadow-sm border" />}
                <div>
                    <label htmlFor="serialNumber" className="block text-sm font-medium text-gray-700 mb-1">Serienummer</label>
                    <input
                        id="serialNumber"
                        type="text"
                        value={formData.serialNumber}
                        onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg text-gray-900 placeholder:text-gray-500"
                        placeholder="Indtast serienummer"
                    />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button onClick={resetState} className="w-full bg-gray-200 text-gray-800 font-bold py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300">
                        Tag nyt billede
                    </button>
                    <button onClick={handleOcrVerification} className="w-full bg-green-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-700 transition-colors duration-300">
                        Bekræft og fortsæt
                    </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'DETAILS':
        return (
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-center text-gray-800">Trin 3: Udfyld Detaljer</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Serienummer</label>
                <p className="text-lg font-mono bg-gray-100 p-2 rounded-md">{formData.serialNumber}</p>
              </div>
              
              <div>
                <label htmlFor="problemDescription" className="block text-sm font-medium text-gray-700">Hvad fejler maskinen?</label>
                <textarea
                  id="problemDescription"
                  rows={4}
                  value={formData.problemDescription}
                  onChange={(e) => setFormData({ ...formData, problemDescription: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-500"
                  placeholder="Vær venligst så detaljeret som muligt. Hvad sker der? Viser displayet en fejlkode?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tag billede af fejlen (valgfrit, max 5)</label>
                 {renderError()}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-2">
                    {errorImages.map((image, index) => (
                        <div key={index} className="relative">
                            <img src={URL.createObjectURL(image)} alt={`Fejl billede ${index + 1}`} className="w-full h-20 object-cover rounded-md" />
                            <button type="button" onClick={() => removeErrorImage(index)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600">
                                <Icon type="trash" className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
                {errorImages.length < 5 && (
                    <button type="button" onClick={() => errorFileInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors">
                        <Icon type="upload" className="w-5 h-5" />
                        Upload billede(r)
                    </button>
                )}
                <input ref={errorFileInputRef} type="file" accept="image/*" multiple onChange={handleErrorImagesChange} className="hidden" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">Navn på kontaktperson</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon type="user" className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="contactName"
                      type="text"
                      value={formData.contactName}
                      onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-500"
                      placeholder="Jytte Hansen"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">Direkte telefonnr.</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon type="phone" className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="contactPhone"
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-500"
                      placeholder="12 34 56 78"
                      required
                    />
                  </div>
                </div>
              </div>

               <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">Din e-mailadresse</label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon type="mail" className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder:text-gray-500"
                      placeholder="din.email@example.com"
                      required
                    />
                  </div>
                </div>

              <div>
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                    Husk mine kontaktoplysninger på denne enhed
                  </label>
                </div>
              </div>

              <button type="submit" className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300 text-lg shadow-md">
                Indsend Fejlmelding
              </button>
            </form>
          </div>
        );

      case 'SUBMITTING':
        return (
            <div className="text-center flex flex-col items-center gap-4">
                <Spinner />
                <p className="text-gray-600 animate-pulse text-lg">Indsender fejlmelding...</p>
            </div>
        );

      case 'SUBMITTED':
        return (
          <div className="text-center">
            <Icon type="check" className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2 text-gray-800">Fejlmelding Afsendt!</h2>
            <p className="text-gray-600 mb-6">Tak for din henvendelse. Vi har modtaget din fejlmelding og vender tilbage hurtigst muligt.</p>
            <button onClick={resetState} className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300">
              Opret en ny fejlmelding
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      {isCameraOpen && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col items-center justify-center">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" aria-label="Kamera visning"></video>
          <canvas ref={canvasRef} className="hidden"></canvas>
          
          <button 
              onClick={closeCamera}
              className="absolute top-4 right-4 bg-black/50 text-white rounded-full p-2 hover:bg-black/75 transition-colors"
              aria-label="Luk kamera"
          >
              <Icon type="close" className="w-8 h-8" />
          </button>

          <div className="absolute bottom-0 left-0 right-0 bg-transparent py-6 flex justify-center">
              <button 
                  onClick={handleCapture}
                  className="p-2 rounded-full bg-white group"
                  aria-label="Tag billede"
              >
                  <div className="w-16 h-16 rounded-full bg-white ring-4 ring-offset-4 ring-offset-black/50 ring-white group-hover:ring-blue-500 transition-all"></div>
              </button>
          </div>
        </div>
      )}
      <div className="w-full max-w-lg mx-auto">
        <header className="text-center mb-8">
            <h1 className="text-5xl font-extrabold text-[#c8102e] mx-auto mb-4 tracking-wider">RICOH</h1>
            <h1 className="text-3xl font-bold text-gray-900">Fejlmeld din printer</h1>
        </header>
        <main className="bg-white rounded-xl shadow-lg p-6 sm:p-8 transition-all duration-300">
          {renderStep()}
        </main>
        <footer className="text-center mt-6">
            <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} RICOH Service</p>
        </footer>
      </div>
    </div>
  );
};

export default App;
